import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import lodashGet from 'lodash.get';
import { Button, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import Form, { CustomFormFieldComponent, CustomValidationFunction, FieldUpdateHandlerParam } from 'shared/form';
import { FormTemplate, FormTemplateSection } from 'data/template/template.types';
import {
  AccessFunctionFieldUpdateCallback,
  AccessFunctionFormData,
  AccessFunctionFormSection,
  AccessFunctionInterfaceFormData,
} from 'data/accessFunction/accessFunction.types';
import './form-section.scss';
import { adaptNetworkIdField, adaptXDPIpAddress } from 'routes/AccessFunction/utils/accessFunctionTemplate.adapter';
import IpAssignmentForm from '../IpAssignmentForm';
import FileImportExportForm from '../FileImportExportForm/FileImportExportForm';

type Props = {
  name: string,
  title?: string,
  hintMessage?: string,
  templateSection: FormTemplateSection | undefined,
  data: Partial<AccessFunctionFormData>,
  formSectionName: AccessFunctionFormSection,
  dataSection?: string | undefined,
  customValidationFunctionMap?: { [key: string]: CustomValidationFunction }
  customFieldComponentMap?: { [key: string]: CustomFormFieldComponent },
  showRemoveButton?: boolean,
  shouldValidateOnRender?: boolean
  i18nKeyPrefix: string
  localizationDisabled?: boolean
  onFieldUpdate?: AccessFunctionFieldUpdateCallback,
  onRemoveButtonClick?: () => void,
  onClickFileAction?: (param: string) => void,
  onFieldCreateNew?: AccessFunctionFieldUpdateCallback,
  template?: FormTemplate,
  mode?: 'view' | 'edit' | 'copy' | 'create',
}

export const FormSection = forwardRef<Form, Props>(({
  name,
  title,
  hintMessage,
  templateSection,
  data,
  formSectionName,
  dataSection = undefined,
  customValidationFunctionMap,
  customFieldComponentMap,
  showRemoveButton = false,
  shouldValidateOnRender = false,
  i18nKeyPrefix,
  localizationDisabled = false,
  mode = 'view',
  onFieldUpdate = () => null,
  onRemoveButtonClick,
  onFieldCreateNew = () => null,
  onClickFileAction = () => null,
  template,
}: Props, ref) => {
  const { t } = useTranslation();

  if (!templateSection) {
    return null;
  }

  const renderTitle = () => {
    if (!title) {
      return null;
    }

    return (
      <div className="form-section-header">
        <div>
          <h3 className="form-section-title">{title}</h3>
          {hintMessage && <p className="form-section-hint-message">{hintMessage}</p>}
        </div>
        {showRemoveButton && (
          <Button data-testid="FormSectionRemove" icon={IconNames.REMOVE} minimal intent={Intent.PRIMARY} onClick={onRemoveButtonClick}>
            {t('accessFunction.form.remove')}
          </Button>
        )}
      </div>
    );
  };

  let formData = data;
  if (dataSection != null) {
    formData = lodashGet(data, dataSection);
  }

  let adaptedTemplateSection = templateSection;
  if ((formSectionName === 'provisioningInterfaces') ||
      (formSectionName === 'dataInterfaces') ||
      (formSectionName === 'contentInterfaces')) {
    adaptedTemplateSection = adaptNetworkIdField(adaptedTemplateSection, formData as AccessFunctionInterfaceFormData);
    adaptedTemplateSection = adaptXDPIpAddress(adaptedTemplateSection, formData as AccessFunctionInterfaceFormData);
  }

  const handleFieldUpdate = (param: FieldUpdateHandlerParam) => {
    if (formData) {
      onFieldUpdate(param, formSectionName, formData.id);
    }
  }

  const handleFieldCreateNew = (param: FieldUpdateHandlerParam) => onFieldCreateNew(
    param, formSectionName, formData.id,
  );

  return (
    <div data-testid={`FormSection-${formSectionName}`}>
      {renderTitle()}
      {formSectionName === 'ipAssignment' ? (
        <IpAssignmentForm
          ref={ref}
          i18nKeyPrefix={i18nKeyPrefix}
          template={template || { version: '', name: '', key: '' }}
          data={data.manualEntry || []}
        />
      ) : (
        formSectionName === 'fileImportExport' ? (
          <div className="form-section-file-import-export">
            <div className="form-section-file-location">
              <Form
                ref={ref}
                name={name}
                defaults={formData}
                fields={adaptedTemplateSection.fields}
                layout={adaptedTemplateSection.metadata.layout?.rows}
                collapsibleLayout={adaptedTemplateSection.metadata.layout?.collapsible}
                additionalSettingsLayout={adaptedTemplateSection.metadata.layout?.additionalSettings}
                fieldUpdateHandler={handleFieldUpdate}
                customValidationFunctionMap={customValidationFunctionMap}
                customFieldComponentMap={customFieldComponentMap}
                shouldValidateOnRender={shouldValidateOnRender}
                fieldCreateNewHandler={handleFieldCreateNew}
                i18nKeyPrefix={i18nKeyPrefix}
              />
            </div>
            <FileImportExportForm
              i18nKeyPrefix={i18nKeyPrefix}
              template={template || { version: '', name: '', key: '' }}
              data={data.manualEntry || []}
              onClickFileAction={onClickFileAction}
              mode={mode}
            />
          </div>
        ) : (
          <Form
            ref={ref}
            name={name}
            defaults={formData}
            fields={adaptedTemplateSection.fields}
            layout={adaptedTemplateSection.metadata.layout?.rows}
            collapsibleLayout={adaptedTemplateSection.metadata.layout?.collapsible}
            additionalSettingsLayout={adaptedTemplateSection.metadata.layout?.additionalSettings}
            fieldUpdateHandler={handleFieldUpdate}
            customValidationFunctionMap={customValidationFunctionMap}
            customFieldComponentMap={customFieldComponentMap}
            shouldValidateOnRender={shouldValidateOnRender}
            fieldCreateNewHandler={handleFieldCreateNew}
            i18nKeyPrefix={i18nKeyPrefix}
            localizationDisabled={localizationDisabled}
          />
        )
      )}
    </div>
  );
});

export default FormSection;
